import type {Caption} from '@remotion/captions';
import React, {useMemo} from 'react';
import {interpolate, useCurrentFrame} from 'remotion';
import {buildCaptionPages, msToFrame} from './caption-utils';

const WORD_BASE: React.CSSProperties = {
	display: 'inline-block',
	whiteSpace: 'pre',
	transition: 'none',
};

export const Captions: React.FC<{
	readonly captions: Caption[];
	readonly accentColor: string;
}> = ({captions, accentColor}) => {
	const frame = useCurrentFrame();
	const pages = useMemo(() => buildCaptionPages(captions), [captions]);

	let pageIndex = -1;
	for (let index = pages.length - 1; index >= 0; index--) {
		const page = pages[index];
		if (frame >= msToFrame(page.startMs) - 6 && frame <= msToFrame(page.endMs) + 20) {
			pageIndex = index;
			break;
		}
	}

	if (pageIndex === -1) {
		return (
			<div style={{height: 208, display: 'flex', alignItems: 'center', justifyContent: 'center'}} />
		);
	}

	const page = pages[pageIndex];
	const pageStart = msToFrame(page.startMs);
	const pageEnd = msToFrame(page.endMs);
	const enter = interpolate(frame, [pageStart - 6, pageStart + 4], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const exit = interpolate(frame, [pageEnd + 10, pageEnd + 20], [1, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	return (
		<div
			style={{
				height: 208,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				opacity: enter * exit,
				transform: `translateY(${interpolate(enter, [0, 1], [14, 0])}px)`,
			}}
		>
			{page.lines.map((line, lineIndex) => (
				<div
					key={lineIndex}
					style={{
						display: 'flex',
						justifyContent: 'center',
						flexWrap: 'nowrap',
						fontSize: 46,
						lineHeight: '66px',
						fontWeight: 600,
						letterSpacing: 2,
					}}
				>
					{line.map((word, wordIndex) => {
						const isCurrent = frame >= msToFrame(word.startMs) && frame < msToFrame(word.endMs);
						const isPast = frame >= msToFrame(word.endMs);
						const color = isCurrent ? accentColor : `rgba(255, 255, 255, ${isPast ? 0.94 : 0.52})`;
						return (
							<span
								key={`${word.startMs}-${wordIndex}`}
								style={{
									...WORD_BASE,
									color,
									transform: `scale(${isCurrent ? 1.06 : 1})`,
									textShadow: isCurrent ? `0 0 28px ${accentColor}aa` : 'none',
								}}
							>
								{word.text}
							</span>
						);
					})}
				</div>
			))}
		</div>
	);
};
